/**
 * Distinguish "no snapshot yet" (first build / new conversation) from real restore failures.
 */
export function isBenignMissingSnapshotError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : '';

  const normalized = message.toLowerCase();

  if (
    normalized.includes('no snapshot') ||
    normalized.includes('snapshot not found')
  ) {
    return true;
  }

  const status =
    error &&
    typeof error === 'object' &&
    'response' in error &&
    error.response &&
    typeof error.response === 'object' &&
    'status' in error.response
      ? Number((error.response as { status?: number }).status)
      : undefined;

  return status === 404;
}

/**
 * A snapshot row exists for the turn, but its stored ZIP could not be downloaded even with a
 * freshly signed URL (the S3 object was deleted — e.g. superseded by a newer snapshot of the
 * same turn — or the URL keeps answering 403/404). Bootstrap restore falls back to an earlier
 * saved version when it sees this. Not "benign": the turn did build, its file is just gone.
 */
export class SnapshotObjectMissingError extends Error {
  readonly llmInteractionId: string;

  constructor(llmInteractionId: string) {
    super("This prototype version's saved file is missing");
    this.name = 'SnapshotObjectMissingError';
    this.llmInteractionId = llmInteractionId;
  }
}

export function isSnapshotObjectMissingError(
  error: unknown,
): error is SnapshotObjectMissingError {
  return (
    error instanceof SnapshotObjectMissingError ||
    (!!error &&
      typeof error === 'object' &&
      (error as { name?: string }).name === 'SnapshotObjectMissingError')
  );
}

/** Restore was superseded by a newer bootstrap or explicit pill restore — not a user failure. */
export function isSupersededRestoreError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return true;
  }
  if (
    error &&
    typeof error === 'object' &&
    'name' in error &&
    (error as { name?: string }).name === 'AbortError'
  ) {
    return true;
  }
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : '';
  return /\baborted\b/i.test(message);
}
